import type { RaceApi } from './apiCore'
import { ApiError } from './apiCore'

export { ApiError, DEMO } from './apiCore'
export type { RaceApi } from './apiCore'

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  let res: Response
  try {
    res = await fetch(url, {
      method,
      headers: body === undefined ? undefined : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'same-origin',
    })
  } catch {
    throw new ApiError('We couldn’t reach the server. Check your connection and try again.', 0)
  }
  const data = res.headers.get('content-type')?.includes('json') ? await res.json() : null
  if (!res.ok) {
    const message =
      data?.message ??
      (typeof data?.error === 'string' && data.error !== 'validation' ? data.error : null) ??
      (res.status === 422 ? 'Some details need fixing.' : `Something went wrong (${res.status}). Please try again.`)
    throw new ApiError(message, res.status, data?.errors ?? {})
  }
  return data as T
}

const http: RaceApi = {
  stats: () => request('GET', '/api/stats'),
  checkCoupon: (code) => request('POST', '/api/coupons/check', { code }),
  checkout: (order) => request('POST', '/api/checkout', order),
  confirmation: (token) => request('GET', `/api/confirmation/${encodeURIComponent(token)}`),
  participants: (q = '') => request('GET', `/api/participants?q=${encodeURIComponent(q)}`),
  volunteer: (v) => request('POST', '/api/volunteers', v),
  notify: (n) => request('POST', '/api/notify', n),
  unsubscribe: (token) => request('POST', '/api/notify/unsubscribe', { token }),
  donate: (d) => request('POST', '/api/donations/checkout', d),
  donation: (token) => request('GET', `/api/donations/${encodeURIComponent(token)}`),
  admin: {
    me: () => request('GET', '/api/admin/me'),
    login: (password) => request('POST', '/api/admin/login', { password }),
    logout: () => request('POST', '/api/admin/logout'),
    summary: () => request('GET', '/api/admin/summary'),
    registrations: (q) => {
      const params = new URLSearchParams()
      if (q.q) params.set('q', q.q)
      if (q.eventId) params.set('eventId', q.eventId)
      if (q.status) params.set('status', q.status)
      return request('GET', `/api/admin/registrations?${params}`)
    },
    update: (id, patch) => request('PATCH', `/api/admin/registrations/${encodeURIComponent(id)}`, patch),
    csvUrl: () => '/api/admin/registrations.csv',
    coupons: () => request('GET', '/api/admin/coupons'),
    createCoupon: (c) => request('POST', '/api/admin/coupons', c),
    setCouponActive: (code, active) => request('PATCH', `/api/admin/coupons/${encodeURIComponent(code)}`, { active }),
    volunteers: () => request('GET', '/api/admin/volunteers'),
    donations: () => request('GET', '/api/admin/donations'),
    donationsCsvUrl: () => '/api/admin/donations.csv',
    notifyList: () => request('GET', '/api/admin/notify'),
    notifyCsvUrl: () => '/api/admin/notify.csv',
    sendNotify: (req) => request('POST', '/api/admin/notify/send', req),
  },
}

let impl: RaceApi = http
// Checked inline (not via DEMO) so the production build drops the demo module entirely.
if (import.meta.env.VITE_DEMO === 'true') {
  // Bundled only into the demo build; the production bundle never includes sample data.
  const { demoApi } = await import('./demoApi')
  impl = demoApi
}

export const api = impl
